import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import { FaBook as FaBookRaw, FaClock as FaClockRaw, FaPlus as FaPlusRaw, FaUserFriends as FaUserFriendsRaw } from 'react-icons/fa';
import { teacherService, type TeacherClassSummary } from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClock: any = FaClockRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '960px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Header = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  border: 1px solid var(--outline);
  background: var(--surface);
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.25rem, 2.4vw, 1.6rem);
  font-weight: 700;
  color: var(--text);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.92rem;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--onPrimary);
  background: var(--primary);
  &:hover {
    background: var(--primaryDark);
  }
`;

const ClassGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const CardLink = styled(Link)`
  color: inherit;
  text-decoration: none;
`;

const ClassCard = styled.article`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  overflow: hidden;
`;

const ClassBanner = styled.div<{ $variant: 'a' | 'b'; $imageUrl?: string | null }>`
  position: relative;
  height: 112px;
  background: ${({ $variant, $imageUrl }) => {
    const gradient =
      $variant === 'b'
        ? 'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 100%)'
        : 'linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (url) {
      return `linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.2) 45%, transparent 100%), url(${url}) center / cover no-repeat, ${gradient}`;
    }
    return gradient;
  }};
`;

const SubjectPill = styled.span`
  position: absolute;
  left: 0.65rem;
  bottom: 0.55rem;
  padding: 0.22rem 0.56rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--primary);
  background: var(--surface);
`;

const CardBody = styled.div`
  padding: 0.9rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ClassTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  color: var(--text);
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const EmptyHint = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.92rem;
`;

function classSubjectLabel(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return 'Class';
  return parts.slice(0, 2).join(' ');
}

const ClassroomTeacherClassesPage: React.FC = () => {
  const classesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'overview'],
    queryFn: teacherService.getOverview,
  });

  if (classesQuery.isLoading) {
    return <EmptyHint>Loading classes…</EmptyHint>;
  }

  if (classesQuery.isError) {
    return <EmptyHint>Unable to load classes.</EmptyHint>;
  }

  const classes = classesQuery.data ?? [];
  const totalStudents = classes.reduce((sum, row) => sum + row.activeStudents, 0);

  return (
    <Page>
      <Header>
        <TitleBlock>
          <Title>Classes</Title>
          <Subtitle>
            {classes.length} active {classes.length === 1 ? 'class' : 'classes'} · {totalStudents} total students
          </Subtitle>
        </TitleBlock>
        <PrimaryButton to="/classroom-teacher/classes/new">
          <FaPlus size={13} aria-hidden />
          New Class
        </PrimaryButton>
      </Header>

      {classes.length === 0 ? (
        <EmptyHint>No classes yet. Create one to start assigning lessons and activities.</EmptyHint>
      ) : (
        <ClassGrid>
          {classes.map((classItem: TeacherClassSummary, index: number) => (
            <CardLink key={classItem.id} to={`/classroom-teacher/classes/${encodeURIComponent(classItem.id)}`}>
              <ClassCard>
                <ClassBanner $variant={index % 2 === 1 ? 'b' : 'a'} $imageUrl={classItem.class_image_url}>
                  <SubjectPill>{classSubjectLabel(classItem.name)}</SubjectPill>
                </ClassBanner>
                <CardBody>
                  <ClassTitle>{classItem.name}</ClassTitle>
                  <MetaRow>
                    <MetaItem>
                      <FaUserFriends size={13} aria-hidden />
                      {classItem.activeStudents} students
                    </MetaItem>
                    <MetaItem>
                      <FaClock size={12} aria-hidden />
                      {classItem.upcomingAssignments} due soon
                    </MetaItem>
                    <MetaItem>
                      <FaBook size={12} aria-hidden />
                      {classItem.recentSubmissions} recent submissions
                    </MetaItem>
                  </MetaRow>
                </CardBody>
              </ClassCard>
            </CardLink>
          ))}
        </ClassGrid>
      )}
    </Page>
  );
};

export default ClassroomTeacherClassesPage;
